/**
 * Application State Type Definitions
 *
 * Types for global application state management.
 * Maps the 27 legacy global variables (g_*) to typed state.
 */

import type { PuzzleData, HistoryState } from './puzzle.types';

/** Main menu state */
export interface MainMenuState {
    isVisible: boolean;
    activeItem: string | null;
}

/** Solution menu state */
export interface SolutionMenuState {
    isVisible: boolean;
    currentSolution: string;
}

/** Clue menu state */
export interface ClueMenuState {
    isVisible: boolean;
    isMarking: boolean; // meghatarozasJeloles
}

/** Current selection state */
export interface SelectionState {
    currentElement: HTMLElement | null; // g_aktualisObj
    currentCellId: number; // g_aktualisMezoID
    currentSelection: number[]; // g_aktualisTomb
    inverseSelection: number[]; // g_aktualisInvTomb
    isHorizontal: boolean; // g_mezoIrany
}

/** Application mode state */
export interface ModeState {
    isBuilderMode: boolean; // g_epitoMod
    isSaved: boolean; // g_mentve
    isVerified: boolean; // g_ellenorizve
}

/** UI settings state */
export interface UISettingsState {
    cellHighlight: boolean; // g_mezoKiemeles
    clueMarking: boolean; // g_meghatarozasJeloles
    syncEditing: boolean; // g_szinkronSzerkesztes
    knownMarking: boolean; // g_ismertJeloles
    maskMarking: boolean; // g_maszkJeloles
    allowImages: boolean; // from puzzle type settings (allowAbra)
    hasGraphics: boolean; // puzzle has graphics/mask
    allowColored: boolean; // from puzzle type settings (allowMegfejtes)
    allowBridge: boolean; // from puzzle type settings (allowHid)
    allowEmpty: boolean; // from puzzle type settings (allowUres)
    allowBlack: boolean; // from puzzle type settings (allowFekete)
    allowSolution: boolean; // from puzzle type settings (allowMegoldas)
    hasMultipleGrids: boolean; // puzzle has more than one grid
}

/** Asset paths and cached images */
export interface AssetState {
    imagePath: string; // g_imgUtvonal
    borderColor: string; // g_keretszin
    coloredBase64: string; // g_base64Szines
    bridgeBase64: string; // g_base64Hid
}

/** Character validation pattern */
export interface ValidationState {
    validCharPattern: string; // g_joKarakterekMezo
}

/** Complete application state */
export interface AppStateData {
    // Core puzzle data
    puzzleId: number; // g_rejtvenyID
    solutionId: number; // g_fejtesID
    puzzle: PuzzleData | null; // g_rejtveny
    settings: Record<string, unknown> | null; // g_beallitas (Settings object)
    history: HistoryState; // g_elozmeny

    // Menu states
    mainMenu: MainMenuState | null; // g_foMenu
    solutionMenu: SolutionMenuState | null; // g_megoldasMenu
    clueMenu: ClueMenuState | null; // g_meghatarozasMenu

    // Selection state
    selection: SelectionState;

    // Mode state
    mode: ModeState;

    // UI settings
    ui: UISettingsState;

    // Assets
    assets: AssetState;

    // Validation
    validation: ValidationState;
}

/** Initial/default application state */
export const DEFAULT_APP_STATE: AppStateData = {
    puzzleId: 0,
    solutionId: 0,
    puzzle: null,
    settings: null,
    history: {
        packages: [],
        currentPackage: null,
        sequenceNumber: -1,
        maxPackages: 50,
    },
    mainMenu: null,
    solutionMenu: null,
    clueMenu: null,
    selection: {
        currentElement: null,
        currentCellId: 0,
        currentSelection: [],
        inverseSelection: [],
        isHorizontal: true,
    },
    mode: {
        isBuilderMode: true,
        isSaved: true,
        isVerified: false,
    },
    ui: {
        cellHighlight: false,
        clueMarking: false,
        syncEditing: false,
        knownMarking: false,
        maskMarking: false,
        allowImages: true,
        hasGraphics: false,
        allowColored: true,
        allowBridge: true,
        allowEmpty: true,
        allowBlack: true,
        allowSolution: true,
        hasMultipleGrids: false,
    },
    assets: {
        imagePath: '/img/',
        borderColor: '',
        coloredBase64: '',
        bridgeBase64: '',
    },
    validation: {
        validCharPattern: '[a-zA-ZáÁéÉíÍúÚüÜűŰóÓöÖőŐ]',
    },
};
